import { Injectable, Injector, inject, signal } from "@angular/core";
import { Observable, ReplaySubject, throwError, of } from "rxjs";
import { catchError, finalize, share, tap, map } from "rxjs/operators";
import { LoggerService } from "./logger.service";
import { AuthService } from "./auth.service";

// Import generated service and models
import { MfaApiService as GeneratedMfaService } from "@api/api/mfa.service";
import {
  MfaEnrollResponse as GeneratedMfaEnrollResponse,
  MfaStatusResponse as GeneratedMfaStatusResponse,
  MfaVerifyResponse as GeneratedMfaVerifyResponse,
  MfaEnrollRequest as GeneratedMfaEnrollRequest,
  MfaVerifyRequest as GeneratedMfaVerifyRequest,
  MfaVerifyEnrollRequest as GeneratedMfaVerifyEnrollRequest,
  MfaDisableRequest as GeneratedMfaDisableRequest,
  BackupCodesResponse as GeneratedBackupCodesResponse,
  RegenerateBackupCodesRequest as GeneratedRegenerateBackupCodesRequest,
} from "@api/model/models";

// ============================================================================
// Local interfaces for backward compatibility
// ============================================================================

/**
 * MFA Enrollment Response from backend
 */
export interface MfaEnrollmentResponse {
  /** Base32-encoded secret for manual entry */
  secret: string;
  /** QR code as data URL (base64 PNG) */
  qr_code_uri: string;
  /** Backup codes for recovery */
  backup_codes: string[];
}

/**
 * MFA Verification Request
 */
export interface MfaVerificationRequest {
  user_id: string;
  code: string;
  remember_device?: boolean;
}

/**
 * MFA Verification Response
 */
export interface MfaVerificationResponse {
  success: boolean;
  message: string;
  /** Token to use for subsequent requests if MFA verified */
  token?: string;
}

/**
 * MFA Status Response
 */
export interface MfaStatusResponse {
  enabled: boolean;
  backup_codes_remaining: number;
}

/**
 * Backup Codes Response
 */
export interface BackupCodesResponse {
  codes: string[];
}

/**
 * MFA Service
 *
 * Handles Multi-Factor Authentication operations including:
 * - TOTP enrollment with QR code
 * - Code verification (TOTP and backup codes)
 * - Backup code management
 * - MFA status checking
 *
 * Delegates to generated MfaApiService for HTTP calls.
 */
@Injectable({
  providedIn: "root",
})
export class MfaService {
  private readonly logger = inject(LoggerService);

  // Reactive state
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);
  readonly mfaEnabled = signal(false);
  readonly backupCodesRemaining = signal(0);

  // Store enrollment data temporarily during setup
  private enrollmentData: MfaEnrollmentResponse | null = null;

  private readonly mfaApi = inject(GeneratedMfaService);

  /**
   * RF-QA-171 · How long one `GET /auth/mfa/status` answer is reused by every
   * caller (route guard, profile load, admin step-up). Long enough to collapse
   * the load-time burst into one request, short enough that a factor change
   * made in another tab is picked up within seconds. Mutations made through
   * this service call {@link invalidateStatus} so they never wait it out.
   */
  private static readonly STATUS_FRESH_MS = 5_000;
  /**
   * `/auth/mfa/status` answers for the SIGNED-IN user, so every cached or
   * in-flight answer is keyed by that user's id. AuthService is resolved
   * lazily (at call time, never at construction) so this root service adds no
   * construction-time dependency on the auth graph.
   */
  private readonly injector = inject(Injector);
  private statusInFlight: {
    readonly principal: string;
    readonly request$: Observable<GeneratedMfaStatusResponse>;
  } | null = null;
  private statusFresh: {
    readonly principal: string;
    readonly at: number;
    readonly value: GeneratedMfaStatusResponse;
  } | null = null;
  /**
   * Bumped by every new request and by {@link invalidateStatus}. A response
   * may only populate the cache if no newer request or invalidation happened
   * while it was in flight (a pre-mutation answer landing after
   * `invalidateStatus()` must not be cached).
   */
  private statusGeneration = 0;

  /** The user a status answer belongs to; null while nobody is signed in. */
  private currentPrincipalId(): string | null {
    return this.injector.get(AuthService).currentUser()?.id ?? null;
  }

  /**
   * RF-QA-171 · Single-flight, briefly cached `GET /auth/mfa/status` on the
   * GENERATED response shape. Concurrent callers for the SAME user share ONE
   * in-flight request; callers arriving within {@link STATUS_FRESH_MS} of the
   * last success for that user reuse it without a network round-trip. An
   * answer is never served to a different user (same-tab logout → login), and
   * errors are never cached.
   */
  fetchStatus(): Observable<GeneratedMfaStatusResponse> {
    const principal = this.currentPrincipalId();
    if (principal === null) {
      // No principal to key an answer on: ask the backend and cache nothing.
      return this.mfaApi.getStatus();
    }
    const fresh = this.statusFresh;
    if (
      fresh !== null &&
      fresh.principal === principal &&
      Date.now() - fresh.at < MfaService.STATUS_FRESH_MS
    ) {
      return of(fresh.value);
    }
    const inFlight = this.statusInFlight;
    if (inFlight !== null && inFlight.principal === principal) {
      return inFlight.request$;
    }
    const generation = ++this.statusGeneration;
    const request$: Observable<GeneratedMfaStatusResponse> = this.mfaApi
      .getStatus()
      .pipe(
        tap((value: GeneratedMfaStatusResponse) => {
          if (generation === this.statusGeneration) {
            this.statusFresh = { principal, at: Date.now(), value };
          }
        }),
        finalize(() => {
          // Only release the slot this request still owns (a newer request
          // or an invalidation may already have replaced it).
          if (this.statusInFlight?.request$ === request$) {
            this.statusInFlight = null;
          }
        }),
        share({
          connector: () => new ReplaySubject<GeneratedMfaStatusResponse>(1),
          resetOnError: true,
          resetOnComplete: false,
          resetOnRefCountZero: false,
        }),
      );
    this.statusInFlight = { principal, request$ };
    return request$;
  }

  /**
   * RF-QA-171 · Drop the cached status so the next {@link fetchStatus} hits
   * the backend, and make any request still in flight unable to repopulate the
   * cache. Called after every factor mutation (enrolment completion, disable,
   * backup-code regeneration here; TOTP/security-key/recovery-code changes in
   * SecuritySettingsService).
   */
  invalidateStatus(): void {
    this.statusGeneration++;
    this.statusFresh = null;
    this.statusInFlight = null;
  }

  /**
   * Start MFA enrollment process
   * Returns QR code and backup codes for setup
   * Requires the user's current password before any TOTP secret is issued.
   */
  startEnrollment(password: string): Observable<MfaEnrollmentResponse> {
    this.isLoading.set(true);
    this.error.set(null);

    const request: GeneratedMfaEnrollRequest = { password };

    return this.mfaApi.startEnrollment(request).pipe(
      map((response: GeneratedMfaEnrollResponse) => {
        // Map generated response to local interface
        const enrollmentResponse: MfaEnrollmentResponse = {
          secret: response.secret,
          qr_code_uri: response.qrCodeUri,
          backup_codes: response.backupCodes,
        };
        return enrollmentResponse;
      }),
      tap((response) => {
        this.enrollmentData = response;
        this.isLoading.set(false);
        this.logger.debug("[MfaService] Enrollment started", {
          secret: "***redacted***",
        });
      }),
      catchError((error) => {
        this.isLoading.set(false);
        const message = this.extractErrorMessage(error);
        this.error.set(message);
        this.logger.error("[MfaService] Enrollment failed", { error: message });
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Complete MFA enrollment by verifying the first TOTP code
   */
  completeEnrollment(
    verificationCode: string,
    password: string,
  ): Observable<boolean> {
    this.isLoading.set(true);
    this.error.set(null);

    const request: GeneratedMfaVerifyEnrollRequest = {
      code: verificationCode,
      password,
    };

    return this.mfaApi.verifyEnrollment(request).pipe(
      map((response) => response.success),
      tap((success) => {
        this.isLoading.set(false);
        if (success) {
          this.mfaEnabled.set(true);
          this.backupCodesRemaining.set(
            this.enrollmentData?.backup_codes.length ?? 0,
          );
          this.enrollmentData = null; // Clear sensitive data
          this.invalidateStatus(); // RF-QA-171: factor state changed
          this.logger.info("[MfaService] Enrollment completed successfully");
        }
      }),
      catchError((error) => {
        this.isLoading.set(false);
        const message = this.extractErrorMessage(error);
        this.error.set(message);
        this.logger.error("[MfaService] Enrollment completion failed", {
          error: message,
        });
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Verify MFA code (TOTP or backup code)
   * Called during login when MFA is required
   * Note: userId parameter kept for backward compatibility but no longer used
   * (generated API uses authenticated user context)
   */
  verifyCode(
    _userId: string,
    code: string,
    rememberDevice: boolean = false,
  ): Observable<MfaVerificationResponse> {
    this.isLoading.set(true);
    this.error.set(null);

    const request: GeneratedMfaVerifyRequest = {
      code,
      rememberDevice,
    };

    return this.mfaApi.verifyCode(request).pipe(
      map((response: GeneratedMfaVerifyResponse) => {
        // Map generated response to local interface
        const verificationResponse: MfaVerificationResponse = {
          success: response.verified,
          message: response.verified
            ? "Verification successful"
            : "Verification failed",
        };
        return verificationResponse;
      }),
      tap((response) => {
        this.isLoading.set(false);
        if (response.success) {
          this.logger.debug("[MfaService] MFA verification successful");
        } else {
          this.error.set(response.message);
        }
      }),
      catchError((error) => {
        this.isLoading.set(false);
        const message = this.extractErrorMessage(error);
        this.error.set(message);
        this.logger.error("[MfaService] Verification failed", {
          error: message,
        });
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Get MFA status for a user
   * Note: userId parameter kept for backward compatibility but no longer used
   * (generated API uses authenticated user context)
   */
  getStatus(_userId?: string): Observable<MfaStatusResponse> {
    // RF-QA-171: goes through the shared single-flight fetch.
    return this.fetchStatus().pipe(
      map((response: GeneratedMfaStatusResponse) => {
        // Map generated response to local interface
        const statusResponse: MfaStatusResponse = {
          enabled: response.enabled,
          backup_codes_remaining: response.backupCodesRemaining,
        };
        return statusResponse;
      }),
      tap((status) => {
        this.mfaEnabled.set(status.enabled);
        this.backupCodesRemaining.set(status.backup_codes_remaining);
        this.logger.debug("[MfaService] Status retrieved", {
          enabled: status.enabled,
        });
      }),
      catchError((error) => {
        const message = this.extractErrorMessage(error);
        // SENTRY-NOISE-CLUSTER: getStatus()'s only caller (mfaRequiredGuard) FAILS
        // OPEN on this error — it allows navigation and logs its own warn, and the
        // backend still enforces MFA on sensitive endpoints. So a transient status
        // fetch failure is a tolerated, non-actionable condition here; log at debug
        // (below the Sentry-forwarding threshold) rather than error. The rejection
        // still propagates so the guard's fail-open branch runs.
        this.logger.debug("[MfaService] Failed to get status (fail-open)", {
          error: message,
        });
        return throwError(
          () => new Error(`Unable to verify MFA status: ${message}`),
        );
      }),
    );
  }

  /**
   * Disable MFA for a user
   * Note: userId parameter kept for backward compatibility but no longer used
   * (generated API uses authenticated user context)
   * @param _userId - Deprecated, no longer used
   * @param password - Current password for verification
   * @param code - TOTP code to confirm identity
   */
  disable(
    _userId?: string,
    password?: string,
    code?: string,
  ): Observable<void> {
    this.isLoading.set(true);
    this.error.set(null);

    const request: GeneratedMfaDisableRequest = {
      password: password || "",
      code: code || "",
    };

    return this.mfaApi.disableMfa(request).pipe(
      map(() => undefined),
      tap(() => {
        this.isLoading.set(false);
        this.mfaEnabled.set(false);
        this.backupCodesRemaining.set(0);
        this.invalidateStatus(); // RF-QA-171: factor state changed
        this.logger.info("[MfaService] MFA disabled");
      }),
      catchError((error) => {
        this.isLoading.set(false);
        const message = this.extractErrorMessage(error);
        this.error.set(message);
        this.logger.error("[MfaService] Failed to disable MFA", {
          error: message,
        });
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Get backup codes for a user
   * Note: Backup codes are only returned during enrollment or regeneration.
   * This method returns cached enrollment data if available.
   * To get fresh backup codes, use regenerateBackupCodes().
   * @param _userId - Deprecated, no longer used
   */
  getBackupCodes(_userId?: string): Observable<string[]> {
    // Return cached enrollment codes if available
    if (this.enrollmentData?.backup_codes) {
      return of(this.enrollmentData.backup_codes);
    }
    // No cached codes available - return error indicating codes need to be regenerated
    this.logger.warn(
      "[MfaService] No cached backup codes available. Use regenerateBackupCodes() to get new ones.",
    );
    return throwError(
      () =>
        new Error(
          "Backup codes are not available. Use regenerateBackupCodes() to generate new codes.",
        ),
    );
  }

  /**
   * Regenerate backup codes
   * Requires the current password; the backend also requires a fresh,
   * session-bound MFA step-up proof.
   */
  regenerateBackupCodes(password: string): Observable<string[]> {
    this.isLoading.set(true);
    this.error.set(null);

    const request: GeneratedRegenerateBackupCodesRequest = { password };

    return this.mfaApi.regenerateBackupCodes(request).pipe(
      map((response: GeneratedBackupCodesResponse) => response.backupCodes),
      tap((codes) => {
        this.isLoading.set(false);
        this.backupCodesRemaining.set(codes.length);
        this.invalidateStatus(); // RF-QA-171: backup-code count changed
        this.logger.info("[MfaService] Backup codes regenerated", {
          count: codes.length,
        });
      }),
      catchError((error) => {
        this.isLoading.set(false);
        const message = this.extractErrorMessage(error);
        this.error.set(message);
        this.logger.error("[MfaService] Failed to regenerate backup codes", {
          error: message,
        });
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Get the cached enrollment data (QR code, secret, backup codes)
   * Only available during active enrollment
   */
  getEnrollmentData(): MfaEnrollmentResponse | null {
    return this.enrollmentData;
  }

  /**
   * Clear any cached enrollment data
   */
  clearEnrollmentData(): void {
    this.enrollmentData = null;
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.error.set(null);
  }

  /**
   * Format a TOTP code for display (add space in middle: 123 456)
   */
  formatCodeForDisplay(code: string): string {
    if (code.length === 6) {
      return `${code.substring(0, 3)} ${code.substring(3)}`;
    }
    return code;
  }

  /**
   * Validate TOTP code format (6 digits)
   */
  isValidTotpCode(code: string): boolean {
    return /^\d{6}$/.test(code);
  }

  /**
   * Validate backup code format (8 alphanumeric characters)
   */
  isValidBackupCode(code: string): boolean {
    return /^[A-Z0-9]{8}$/i.test(code);
  }

  /**
   * Extract user-friendly error message from HTTP error
   */
  private extractErrorMessage(error: unknown): string {
    // Handle HttpErrorResponse with nested error object
    if (this.isHttpErrorWithNestedMessage(error)) {
      return error.error.message;
    }
    // Handle Error objects or HttpErrorResponse with direct message
    if (this.hasMessage(error)) {
      return error.message;
    }
    // Handle string errors
    if (typeof error === "string") {
      return error;
    }
    return "An unexpected error occurred. Please try again.";
  }

  /** Type guard for objects with nested error.message (HttpErrorResponse pattern) */
  private isHttpErrorWithNestedMessage(
    error: unknown,
  ): error is { error: { message: string } } {
    if (typeof error !== "object" || error === null || !("error" in error)) {
      return false;
    }
    const errorObj = (error as Record<string, unknown>)["error"];
    if (
      typeof errorObj !== "object" ||
      errorObj === null ||
      !("message" in errorObj)
    ) {
      return false;
    }
    return typeof (errorObj as Record<string, unknown>)["message"] === "string";
  }

  /** Type guard for objects with message property (Error or HttpErrorResponse) */
  private hasMessage(error: unknown): error is { message: string } {
    return (
      typeof error === "object" &&
      error !== null &&
      "message" in error &&
      typeof (error as Record<string, unknown>)["message"] === "string"
    );
  }
}
